import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sparkles, CheckCircle, AlertCircle, XCircle } from 'lucide-react';

const PRIORITY_COLORS = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-gray-100 text-gray-700',
};

const STATUS_COLORS = {
  submitted: 'bg-gray-100 text-gray-700',
  analyzed: 'bg-blue-100 text-blue-700',
  accepted: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  in_development: 'bg-purple-100 text-purple-700',
};

const RECOMMENDATION_ICONS = {
  Accept: { icon: CheckCircle, color: 'text-green-600' },
  Review: { icon: AlertCircle, color: 'text-yellow-600' },
  Decline: { icon: XCircle, color: 'text-red-600' },
};

export function IdeaDetailModal({ idea, user, modules, analyzing, onClose, onAnalyze, onCreateTask, onUpdateStatus }) {
  if (!idea) return null;

  return (
    <Dialog open={!!idea} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="idea-detail-modal">
        <DialogHeader>
          <DialogTitle className="text-xl">{idea.title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="flex flex-wrap gap-2">
            <Badge className={PRIORITY_COLORS[idea.priority]}>{idea.priority}</Badge>
            <Badge className={STATUS_COLORS[idea.status]}>{idea.status}</Badge>
            <Badge variant="outline">{idea.category}</Badge>
            {idea.restaurant_name && <Badge variant="outline">{idea.restaurant_name}</Badge>}
            {idea.submitted_by_name && <Badge variant="outline">Submitted by: {idea.submitted_by_name}</Badge>}
          </div>

          <div className="space-y-4">
            <div>
              <h4 className="font-semibold text-[#111111] mb-2">Problem Description</h4>
              <p className="text-sm text-[#666666]">{idea.problem_description}</p>
            </div>
            <div>
              <h4 className="font-semibold text-[#111111] mb-2">Proposed Solution</h4>
              <p className="text-sm text-[#666666]">{idea.proposed_solution}</p>
            </div>
            <div>
              <h4 className="font-semibold text-[#111111] mb-2">Business Impact</h4>
              <p className="text-sm text-[#666666]">{idea.business_impact}</p>
            </div>
          </div>

          {user?.role !== 'restaurant' && idea.ai_analysis && (
            <div className="bg-[#F3F8FF] rounded-lg p-4 space-y-4">
              <div className="flex items-center gap-2">
                <Sparkles className="text-[#3B82F6]" size={20} />
                <h4 className="font-semibold text-[#111111]">AI Analysis</h4>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: 'Feasibility', key: 'feasibility_score' },
                  { label: 'Business Value', key: 'business_value_score' },
                  { label: 'Technical Complexity', key: 'technical_complexity_score' },
                  { label: 'Overall Priority', key: 'overall_priority_score' },
                ].map(({ label, key }) => (
                  <div key={key}>
                    <p className="text-xs text-[#666666] mb-1">{label}</p>
                    <div className="flex items-center gap-2">
                      <Progress value={idea.ai_analysis[key]} className="h-2" />
                      <span className={`text-sm font-medium ${key === 'overall_priority_score' ? (
                        idea.ai_analysis[key] >= 70 ? 'text-green-600 text-lg font-bold' :
                        idea.ai_analysis[key] >= 50 ? 'text-yellow-600 text-lg font-bold' :
                        'text-red-600 text-lg font-bold'
                      ) : ''}`}>
                        {idea.ai_analysis[key]}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="pt-2 border-t border-[#E0E0E0]">
                {(() => {
                  const rec = RECOMMENDATION_ICONS[idea.ai_analysis.recommendation];
                  const RecIcon = rec?.icon;
                  return RecIcon && (
                    <div className={`flex items-center gap-1 mb-2 ${rec.color}`}>
                      <RecIcon size={20} />
                      <span className="font-semibold">{idea.ai_analysis.recommendation}</span>
                    </div>
                  );
                })()}
                <p className="text-sm text-[#666666]">{idea.ai_analysis.reasoning}</p>
                <div className="flex gap-4 mt-3 text-sm">
                  <span><strong>Est. Dev:</strong> {idea.ai_analysis.estimated_dev_weeks} weeks</span>
                  <span><strong>Revenue:</strong> {idea.ai_analysis.revenue_potential}</span>
                  <span><strong>Module:</strong> {idea.ai_analysis.affected_module}</span>
                </div>
              </div>
            </div>
          )}

          {(user?.role === 'admin' || user?.role === 'partner') && (
            <div className="flex flex-wrap gap-2 pt-4 border-t border-[#E0E0E0]">
              {!idea.ai_analysis && (
                <Button onClick={() => onAnalyze(idea.id)} disabled={analyzing} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white">
                  <Sparkles size={16} className="mr-2" />{analyzing ? 'Analyzing...' : 'Analyze with AI'}
                </Button>
              )}
              {idea.ai_analysis?.recommendation === 'Accept' && user?.role === 'admin' && (
                <Select onValueChange={(moduleId) => onCreateTask(idea.id, moduleId)}>
                  <SelectTrigger className="w-48"><SelectValue placeholder="Move to Module..." /></SelectTrigger>
                  <SelectContent>{modules.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                </Select>
              )}
              {idea.status === 'submitted' && (
                <Button variant="outline" onClick={() => onUpdateStatus(idea.id, 'rejected')} className="text-red-600 border-red-200">Reject</Button>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
